// @flow
import * as React from 'react';
import { Route } from '@ta/lithium.context';
import usePixelCallback from './usePixelCallback';

const debugCounter = {};

const usePageViewMoniker = <T: ?{ +[key: string]: string }>(
  config: T,
  enabled?: boolean = true,
) => {
  const [, { uid }] = React.useContext(Route);
  const onPageView = usePixelCallback({ type: 'PAGEVIEW', payload: config || {} });
  const latestOnPageView = React.useRef(onPageView);
  // Enforce 1 page view fired <-> puid
  const firedForUid = React.useRef(new Set());
  // Use effects to keep the callback up to date; because the callback is proxy'd into a ref, we don't need to list it as a dependency
  // You should still memoize the config anyway
  React.useEffect(() => {
    latestOnPageView.current = onPageView;
  }, [onPageView]);

  // We truly only want this effect to fire when uid changes!
  React.useEffect(() => {
    if (enabled && !firedForUid.current.has(uid)) {
      latestOnPageView.current();
      firedForUid.current.add(uid);
    }
    if (DEBUG) {
      if (enabled && debugCounter[uid]) {
        throw new Error('Only one usePageViewMoniker hook should fire per PUID!');
      }
      debugCounter[uid] = enabled;
    }
  }, [uid, enabled]);
};

export default usePageViewMoniker;
